// src/components/Sales/InvoiceDetailsModal.jsx
import React from 'react';
import { X, User, Calendar, ShoppingCart, AlertCircle } from 'lucide-react';
import './InvoiceDetailsModal.css';

const InvoiceDetailsModal = ({ isOpen, onClose, invoice }) => {
  if (!isOpen || !invoice) return null;

  // Calculate payment summary
  const calculatePaymentSummary = () => {
    const summary = {
      cash: 0,
      credit: 0,
      cheque: 0,
      total: 0
    };

    // Calculate from sale items based on their payment method
    if (invoice.sale && Array.isArray(invoice.sale)) {
      invoice.sale.forEach(item => {
        const itemTotal = Number(item.qty || 0) * Number(item.price || 0);
        
        if (item.paymentMethod === 'CASH') {
          summary.cash += itemTotal;
        } else if (item.paymentMethod === 'CREDIT') {
          summary.credit += itemTotal;
        } else if (item.paymentMethod === 'CHEQUE') {
          summary.cheque += itemTotal;
        }
      });
    }

    // Total from invoice
    summary.total = Number(invoice.totalValue || 0);

    return summary;
  };

  const paymentSummary = calculatePaymentSummary();

  // Determine payment method
  const getPaymentMethod = () => {
    const hasMultiplePaymentMethods = [];
    
    if (paymentSummary.cash > 0) hasMultiplePaymentMethods.push('CASH');
    if (paymentSummary.credit > 0) hasMultiplePaymentMethods.push('CREDIT');
    if (paymentSummary.cheque > 0) hasMultiplePaymentMethods.push('CHEQUE');
    
    // If more than one payment method has a value, it's MIXED
    if (hasMultiplePaymentMethods.length > 1) return 'MIXED';
    
    // Otherwise return the single payment method
    if (hasMultiplePaymentMethods.length === 1) return hasMultiplePaymentMethods[0];
    
    // Default to CASH if nothing
    return 'CASH';
  };

  const formatCurrency = (amount) => {
    return `Rs. ${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <div className="invoice-modal-overlay" onClick={onClose}>
      <div className="invoice-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="invoice-modal-header">
          <div>
            <h2>Invoice Details</h2>
            <p className="invoice-number">{invoice.invoiceNumber}</p>
          </div>
          <button className="invoice-modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="invoice-modal-body">
          {/* Customer Information & Invoice Date */}
          <div className="invoice-info-row">
            <div className="invoice-info-card">
              <div className="invoice-info-header">
                <User size={18} />
                <span>Customer Information</span>
              </div>
              <p className="invoice-info-value">
                {invoice.customer?.shopName || invoice.customer?.name || 'Cash Customer'}
              </p>
            </div>

            <div className="invoice-info-card">
              <div className="invoice-info-header">
                <Calendar size={18} />
                <span>Invoice Date</span>
              </div>
              <p className="invoice-info-value">
                {formatDate(invoice.createdAt)}
              </p>
            </div>
          </div>

          {/* Payment Summary */}
          <div className="payment-summary-section">
            <div className="section-header">
              <AlertCircle size={18} />
              <span>Payment Summary</span>
            </div>
            <div className="payment-summary-grid">
              <div className="payment-item">
                <span className="payment-label">Cash</span>
                <span className="payment-value cash">{formatCurrency(paymentSummary.cash)}</span>
              </div>
              <div className="payment-item">
                <span className="payment-label">Credit</span>
                <span className="payment-value credit">{formatCurrency(paymentSummary.credit)}</span>
              </div>
              <div className="payment-item">
                <span className="payment-label">Cheque</span>
                <span className="payment-value cheque">{formatCurrency(paymentSummary.cheque)}</span>
              </div>
              <div className="payment-item">
                <span className="payment-label">Total</span>
                <span className="payment-value total">{formatCurrency(paymentSummary.total)}</span>
              </div>
            </div>
            <div className="payment-method-badge">
              <span className="payment-method-label">Payment Method:</span>
              <span className="payment-method-value">{getPaymentMethod()}</span>
            </div>
          </div>

          {/* Returns Section - Only show if returns exist */}
          {invoice.return && invoice.return.length > 0 && (
            <div className="returns-section">
              <div className="section-header">
                <AlertCircle size={18} />
                <span>Returns ({invoice.return.length})</span>
              </div>
              <div className="returns-list">
                {invoice.return.map((returnItem, index) => (
                  <div key={returnItem.id || index} className="return-item">
                    <div className="return-item-info">
                      <p className="return-item-name">Return #{returnItem.id}</p>
                      <p className="return-item-details">
                        Customer ID: {returnItem.customerId} • 
                        Inventory ID: {returnItem.inventoryId} • 
                        Date: {formatDate(returnItem.createdAt)}
                      </p>
                    </div>
                    <span className="return-item-total">
                      {formatCurrency(returnItem.amount || 0)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Purchased Items */}
          <div className="purchased-items-section">
            <div className="section-header">
              <ShoppingCart size={18} />
              <span>Purchased Items ({invoice.sale?.length || 0})</span>
            </div>
            <div className="purchased-items-list">
              {invoice.sale && invoice.sale.map((item, index) => (
                <div key={index} className="purchased-item">
                  <div className="purchased-item-main">
                    <p className="purchased-item-name">{item.product?.name || 'N/A'}</p>
                    <span className="purchased-item-price">{formatCurrency((item.qty || 0) * (item.price || 0))}</span>
                  </div>
                  <div className="purchased-item-details">
                    <p className="purchased-item-sku">SKU: {item.product?.sku || 'N/A'}</p>
                    {/* <p className="purchased-item-category">Category: {item.product?.category?.name || 'N/A'}</p> */}
                  </div>
                  <div className="purchased-item-quantity">
                    <span>Qty: {item.qty || 0}</span>
                    <span>Unit Price: {formatCurrency(item.price || 0)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="invoice-modal-footer">
          <button className="btn-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetailsModal;